import { es, enUS } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Radio, useMediaQuery } from '@mui/material'
import { ChangeEvent, useContext, useState } from 'react'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import Dialog from '../common/dialog'
import i18n from '../../utils/i18n.utils'
import { countriesCurrencies } from '../../lib'
import { FormError, ReportFilters } from '../../types'
import { requestAccessToken } from '../../api/auth.api'
import { textFieldWhiteBg } from '../../theme/custom-styles'
import { createAndDownloadFundingsReport } from '../../api/payments.api'
import { AuthContext, NotificationsContext, UserContext } from '../../contexts'

export const CustomerCompanyFundingsHistoryReportModal = ({ customerCompanyId, handleSetModalOpenState }: { customerCompanyId?: string; handleSetModalOpenState: (openState: boolean) => void }) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toUpperCase()

  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { t } = useTranslation(['reports-management', 'common'])
  const { activateAuth, isAuth, removeAuth } = useContext(AuthContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const mdDown = useMediaQuery((theme: any) => theme.breakpoints.down('md'))

  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<FormError>({})
  const [selectedFileType, setSelectedFileType] = useState('pdf')
  const [toDateToFilter, setToDateToFilter] = useState<Date | null>(null)
  const [fromDateToFilter, setFromDateToFilter] = useState<Date | null>(null)

  const handleDownloadRequestedReport = async () => {
    setErrors({})
    let formDataIsValid = true
    const formErrors: FormError = {}
    const reportFilters = {
      toDateToFilter,
      fromDateToFilter,
      selectedFileType
    } as ReportFilters

    if (!fromDateToFilter) {
      formDataIsValid = false
      formErrors.fromDateToFilter = t('modals.paymentsHistory.errors.fromDateToFilter')
    }

    if (!toDateToFilter) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilter')
    }

    if (fromDateToFilter && toDateToFilter && toDateToFilter?.getTime() < fromDateToFilter?.getTime()) {
      formDataIsValid = false
      formErrors.toDateToFilter = t('modals.paymentsHistory.errors.toDateToFilterEarlier')
    }

    if (!formDataIsValid) {
      setErrors(formErrors)
      return
    }

    try {
      setIsLoading(true)
      const {
        data: { accessToken, refreshToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      activateAuth?.({ token: refreshToken })

      const fundingsHistoryReportDocumentToDownload = await createAndDownloadFundingsReport(
        { currency, reportFilters, reportLanguage: i18n.language, belongsTo: String(userSession?.belongsTo) === 'holdings' ? String(customerCompanyId) : String(userSession?.belongsTo) },
        accessToken
      )

      if (fundingsHistoryReportDocumentToDownload.data === 'Empty report') {
        setErrors({ generalError: t('formGeneralErrors.emptyReport', { ns: 'common' }) })
        setIsLoading(false)
        return
      }

      window.open(fundingsHistoryReportDocumentToDownload.data, '_blank')
      updateNewNotificationStatus?.(true, 'payments-history-report-created-success')
      handleSetModalOpenState(false)
    } catch (error: any) {
      console.log('Error handleDownloadRequestedReport', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401 && error?.response?.data.message === 'Not authorized: Exists') {
        setErrors({ generalError: `${t('formGeneralErrors.unauthorized', { ns: 'common' })}: 406` })
      } else if (error?.response?.status === 401 && !(error?.response?.data.message === 'Not authorized: Exists')) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setIsLoading(false)
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Dialog
      errors={errors}
      maxHeight="865px"
      closeOptionWarning={true}
      dialogIsLoading={isLoading}
      minWidth={mdDown ? '0px' : '800px'}
      dialogTitle={t('modals.paymentsHistory.titleFundings')}
      handleDialogAction={handleDownloadRequestedReport}
      handleCloseOptionAction={() => handleSetModalOpenState(false)}
      actionButtonText={t('modals.paymentsHistory.buttons.download')}
      handleDialogOpenState={(openState: boolean) => handleSetModalOpenState(openState)}
    >
      <div className="payments-history-modal-container">
        <div className="payments-history-modal-full-column-container">
          <div className="payments-history-modal-options-column">
            <div className="payments-history-modal-subtitle">{t('modals.paymentsHistory.subtitles.fileType')}</div>
            <div className="payments-history-modal-options-row">
              <div
                onClick={() => {
                  setErrors({})
                  setToDateToFilter(null)
                  setFromDateToFilter(null)
                  setSelectedFileType('pdf')
                }}
                style={{ cursor: 'pointer' }}
                className="payments-history-modal-full-column-container"
              >
                <Radio
                  size="small"
                  checked={selectedFileType === 'pdf'}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedFileType(event.target.value)}
                  value="pdf"
                  name="radio-buttons"
                  inputProps={{ 'aria-label': 'PDF' }}
                  sx={{
                    '&.Mui-checked': {
                      color: '#2ed3b7'
                    }
                  }}
                />
                <div>PDF</div>
              </div>
              <div
                onClick={() => {
                  setErrors({})
                  setToDateToFilter(null)
                  setFromDateToFilter(null)
                  setSelectedFileType('csv')
                }}
                style={{ cursor: 'pointer' }}
                className="payments-history-modal-full-column-container"
              >
                <Radio
                  size="small"
                  checked={selectedFileType === 'csv'}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setSelectedFileType(event.target.value)}
                  value="csv"
                  name="radio-buttons"
                  inputProps={{ 'aria-label': 'CSV' }}
                  sx={{
                    '&.Mui-checked': {
                      color: '#2ed3b7'
                    }
                  }}
                />
                <div>CSV</div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="payments-history-modal-full-column-container"
          style={{ alignItems: 'flex-end', gap: '20px' }}
        >
          <div className="payments-history-modal-options-column">
            <div className="payments-history-modal-subtitle">{t('modals.paymentsHistory.subtitles.selectSettlementDateRange')}</div>
            <div>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
              >
                <DatePicker
                  label={t('modals.paymentsHistory.options.fromDate')}
                  sx={{ width: '100%' }}
                  onChange={(newValue) => setFromDateToFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'standard',
                      error: !!errors.fromDateToFilter,
                      helperText: errors.fromDateToFilter,
                      sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
          <div className="payments-history-modal-options-column">
            <div>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={i18n && i18n.language.indexOf('es') !== -1 ? es : enUS}
              >
                <DatePicker
                  label={t('modals.paymentsHistory.options.toDate')}
                  sx={{ width: '100%' }}
                  onChange={(newValue) => setToDateToFilter(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'standard',
                      error: !!errors.toDateToFilter,
                      helperText: errors.toDateToFilter,
                      sx: { ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
